import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaPlus, FaSpinner, FaTimes, FaLink, FaBarcode,
  FaMoneyCheckAlt, FaRegFileAlt, FaChevronDown,
  FaUniversity, FaArrowRight, FaEye, FaCalendarAlt, FaCheck,
  FaTruck, FaParking, FaFilter, FaMagic, FaPaperclip,
  FaFileInvoiceDollar, FaExchangeAlt, FaExclamationCircle,
  FaPen, FaTrashAlt, FaCheckCircle, FaUserTie,
} from 'react-icons/fa';
import ModalContasAReceber from './ModalContasAReceber';
import ModalConversaoConta from './ModalConversaoConta';
import WizardLancamento, { WizardTriggerBtn } from './WizardLancamento';
import './ContasAReceber.css';
import RegenteLancamento from './RegenteLancamento';

/* ─── Helpers ───────────────────────────────────────────────── */
const fmtCur = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const todayISO = () => new Date().toISOString().split('T')[0];

const monthRange = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const last = new Date(y, d.getMonth() + 1, 0).getDate();
  return { start: `${y}-${m}-01`, end: `${y}-${m}-${last}` };
};

const quarterRange = () => {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const s = new Date(now.getFullYear(), q * 3, 1);
  const e = new Date(now.getFullYear(), q * 3 + 3, 0);
  return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] };
};

const yearRange = () => {
  const y = new Date().getFullYear();
  return { start: `${y}-01-01`, end: `${y}-12-31` };
};

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

/* ─── Constantes ─────────────────────────────────────────────── */
const QUICK_PERIODS = [
  { id: 'mes',  label: 'Mês',   fn: () => monthRange(0) },
  { id: 'trim', label: 'Trim.', fn: quarterRange },
  { id: 'ano',  label: 'Ano',   fn: yearRange },
];

const STATUS_CFG = {
  pendente: { label: 'Pendente', cls: 'pill-pending'  },
  vencido:  { label: 'Vencido',  cls: 'pill-overdue'  },
  recebido: { label: 'Recebido', cls: 'pill-received' },
};

/* ─── OrigemTag ──────────────────────────────────────────────── */
const OrigemTag = ({ conta }) => {
  if (conta.origem_tipo === 'patio' && conta.origem_id)
    return (
      <span className="orig-tag patio" title={`Diárias de pátio — OS #${conta.origem_id}`}>
        <FaParking size={10} /> Pátio #{conta.origem_id}
      </span>
    );
  if (conta.origem_tipo === 'os_servico' && conta.origem_id)
    return (
      <span className="orig-tag os" title={`OS #${conta.origem_id}`}>
        <FaTruck size={10} /> OS #{conta.origem_id}
      </span>
    );
  // [APROVAÇÃO] Recebível nascido da aprovação de uma A/P (reembolso ao prestador)
  if (conta.origem_tipo === 'aprovacao_ap' && conta.origem_id)
    return (
      <span className="orig-tag aprovacao" title={`Reembolso aprovado — Conta a Pagar #${conta.origem_id}`}>
        <FaCheckCircle size={10} /> Reembolso A/P #{conta.origem_id}
      </span>
    );
  if (['venda_manual', 'manual'].includes(conta.origem_tipo))
    return <span className="orig-tag boleto"><FaBarcode size={10} /> Manual</span>;
  return <span className="orig-tag other"><FaRegFileAlt size={10} /> Outro</span>;
};

/* ─── BaixaModal ─────────────────────────────────────────────── */
const BaixaModal = ({ conta, onConfirm, onClose, loading }) => {
  const [forma, setForma] = useState('pix');
  const [obs,   setObs]   = useState('');
  const [dtRec, setDtRec] = useState(todayISO());

  const FORMAS = [
    { id: 'pix',           label: 'PIX'          },
    { id: 'transferencia', label: 'Transferência' },
    { id: 'boleto',        label: 'Boleto'        },
    { id: 'cartao',        label: 'Cartão'        },
    { id: 'dinheiro',      label: 'Dinheiro'      },
    { id: 'outro',         label: 'Outro'         },
  ];

  return (
    <div className="cnc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="baixa-modal">
        <div className="baixa-modal-head">
          <FaMoneyCheckAlt className="bm-icon" />
          <div>
            <h3>Registrar Recebimento</h3>
            <p>{conta.descricao}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>

        {conta.origem_tipo === 'os_servico' && (
          <div className="bm-os-banner">
            <FaTruck />
            <span>
              OS <strong>#{conta.origem_id}</strong>.{' '}
              {conta.financeiro_id
                ? 'Financeiro já registrado — será conciliado.'
                : 'Lançamento criado automaticamente.'}
            </span>
          </div>
        )}
        {conta.origem_tipo === 'patio' && (
          <div className="bm-os-banner bm-patio-banner">
            <FaParking />
            <span>Diárias de pátio — <strong>OS #{conta.origem_id}</strong>.</span>
          </div>
        )}
        {conta.origem_tipo === 'aprovacao_ap' && (
          <div className="bm-os-banner bm-aprovacao-banner">
            <FaCheckCircle />
            <span>
              Reembolso aprovado — <strong>A/P #{conta.origem_id}</strong>.
              {conta.pagador_nome && <> Pagador: <strong>{conta.pagador_nome}</strong>.</>}
            </span>
          </div>
        )}

        <div className="bm-value-row">
          <span>Valor a receber</span>
          <strong className="bm-value">{fmtCur(conta.valor)}</strong>
        </div>

        <div className="bm-form">
          <label>
            Forma de pagamento
            <select value={forma} onChange={e => setForma(e.target.value)}>
              {FORMAS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </label>
          <label>
            Data de recebimento
            <input type="date" value={dtRec} onChange={e => setDtRec(e.target.value)} max={todayISO()} />
          </label>
          <label>
            Observação <span className="bm-opt">(opcional)</span>
            <input
              type="text" maxLength={200}
              placeholder="Nº comprovante, banco, etc."
              value={obs} onChange={e => setObs(e.target.value)}
            />
          </label>
        </div>

        <div className="bm-actions">
          <button className="bm-btn-cancel" onClick={onClose} disabled={loading}>Cancelar</button>
          <button
            className="bm-btn-confirm"
            onClick={() => onConfirm({ forma_pagamento: forma, observacao: obs, data_recebimento: dtRec })}
            disabled={loading}
          >
            {loading ? <FaSpinner className="spin" /> : <FaCheck />}
            Confirmar recebimento
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Componente Principal ───────────────────────────────────── */
export default function ContasAReceber() {
  /* ── Filters ── */
  const mr = monthRange(0);
  const [filters, setFilters] = useState({
    dataInicio:  mr.start,
    dataFim:     mr.end,
    status:      '',
    origem_tipo: '',
    numero_nota: '',
  });
  const filtersRef     = useRef(filters);
  const [filtersOpen,  setFiltersOpen]  = useState(false);
  const [activePeriod, setActivePeriod] = useState('mes');

  useEffect(() => { filtersRef.current = filters; }, [filters]);

  /* ── Data state ── */
  const [kpis,            setKpis]            = useState({ total_pendente: 0, total_vencido: 0, vence_hoje: 0, recebido_mes: 0 });
  const [contas,          setContas]          = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [contaDestino,    setContaDestino]    = useState('');
  const [loadingKpi,      setLoadingKpi]      = useState(true);
  const [loadingTable,    setLoadingTable]    = useState(true);
  const [modalOpen,       setModalOpen]       = useState(false);
  const [editItem,        setEditItem]        = useState(null);
  const [modalMode,       setModalMode]       = useState(null);   /* null = criar/baixa · 'editar' = edição */
  const [selected,        setSelected]        = useState(new Set());
  const [submitting,      setSubmitting]      = useState(false);
  const [wizardOpen,      setWizardOpen]      = useState(false);

  /* ── currentUser + permissão de conversão ── */
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    try { setCurrentUser(JSON.parse(localStorage.getItem('user') || '{}')); } catch {}
  }, []);
  const isAdminGeral = ['admin_geral', 'admin'].includes(currentUser?.perfil);

  /* ── Conversão A/R → A/P ── */
  const [conversaoTarget, setConversaoTarget] = useState(null);

  /* ── Baixa ── */
  const [baixaTarget,  setBaixaTarget]  = useState(null);
  const [baixaLoading, setBaixaLoading] = useState(false);

  /* ── Exclusão ── */
  const [deletingId, setDeletingId] = useState(null);

  /* ── AI Search ── */
  const [iaBusca,   setIaBusca]   = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [iaResult,  setIaResult]  = useState(null);
  const [iaAtivo,   setIaAtivo]   = useState(false);

  const debounceRef = useRef(null);
  const location    = useLocation();

  /* ── Filter helpers ── */
  const setFilter = (k, v) => {
    setFilters(f => ({ ...f, [k]: v }));
    if (k !== 'dataInicio' && k !== 'dataFim') return;
    setActivePeriod('');
  };

  const applyPeriod = (id) => {
    const preset = QUICK_PERIODS.find(p => p.id === id);
    if (!preset) return;
    const { start, end } = preset.fn();
    setFilters(f => ({ ...f, dataInicio: start, dataFim: end }));
    setActivePeriod(id);
  };

  const activeFilterCount   = [filters.status, filters.origem_tipo, filters.numero_nota].filter(Boolean).length;
  const hasSecondaryFilters = activeFilterCount > 0;

  const clearSecondaryFilters = () =>
    setFilters(f => ({ ...f, status: '', origem_tipo: '', numero_nota: '' }));

  /* ── Fetch ── */
  const fetchKpis = useCallback(async () => {
    setLoadingKpi(true);
    try {
      const { data } = await api.get('/api/financeiro/kpis-receber');
      setKpis(data);
    } catch { toast.error('Erro ao carregar indicadores.'); }
    finally { setLoadingKpi(false); }
  }, []);

  const fetchContas = useCallback(async (flt) => {
    setLoadingTable(true);
    try {
      const params = {};
      if (flt.status)      params.status      = flt.status;
      if (flt.dataInicio)  params.dataInicio   = flt.dataInicio;
      if (flt.dataFim)     params.dataFim      = flt.dataFim;
      if (flt.numero_nota) params.numero_nota  = flt.numero_nota;
      if (flt.origem_tipo) params.origem_tipo  = flt.origem_tipo;
      const res = await api.get('/api/contas-a-receber', { params });
      setContas(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch { toast.error('Erro ao carregar contas.'); }
    finally { setLoadingTable(false); }
  }, []);

  const fetchBancos = useCallback(async () => {
    try {
      const { data } = await api.get('/api/financeiro/conexoes');
      setContasBancarias((Array.isArray(data) ? data : []).filter(b => b.status === 'conectado' || b.conectado));
    } catch {}
  }, []);

  const reload = useCallback(() => {
    fetchKpis();
    fetchContas(filtersRef.current);
    setSelected(new Set());
  }, [fetchKpis, fetchContas]);

  useEffect(() => { fetchBancos(); }, [fetchBancos]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchKpis();
      fetchContas(filters);
      setSelected(new Set());
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [filters, fetchKpis, fetchContas]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get('refresh') === '1') {
      reload();
      toast.info('Lista atualizada após conciliação.');
    }
  }, [location.search, reload]);

  /* ── AI Search ── */
  const handleIaBusca = async () => {
    if (!iaBusca.trim()) return;
    setIaLoading(true);
    try {
      const { data } = await api.post('/api/contas-a-receber/ia-busca', { query: iaBusca });
      setIaResult(data);
      setIaAtivo(true);
      if (data.total_encontrado === 0) toast.info('IA não encontrou contas para essa busca.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro na busca com IA.');
    } finally {
      setIaLoading(false);
    }
  };

  const clearIaBusca = () => {
    setIaBusca('');
    setIaResult(null);
    setIaAtivo(false);
  };

  /* ── KPI click → toggle status filter ── */
  const handleKpiClick = (s) => setFilter('status', filters.status === s ? '' : s);

  /* ── Baixa individual ── */
  const handleBaixa = async ({ forma_pagamento, observacao, data_recebimento }) => {
    if (!baixaTarget) return;
    setBaixaLoading(true);
    try {
      const { data } = await api.post(`/financeiro/contas-a-receber/${baixaTarget.id}/baixa`, {
        forma_pagamento, observacao, data_recebimento, origem: 'manual',
      });
      toast.success(data.message || 'Recebimento registrado!');
      setBaixaTarget(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao registrar baixa.');
    } finally {
      setBaixaLoading(false);
    }
  };

  /* ── Abrir modal de edição ── */
  const handleEditar = (conta) => {
    setEditItem(conta);
    setModalMode('editar');
    setModalOpen(true);
  };

  /* ── Excluir conta (Admin / Admin Geral) ── */
  const handleExcluir = async (conta) => {
    const ok = window.confirm(
      `Excluir a conta "${conta.descricao}" no valor de ${fmtCur(conta.valor)}?\n\n` +
      `Esta ação não pode ser desfeita.`
    );
    if (!ok) return;

    setDeletingId(conta.id);
    try {
      const { data } = await api.delete(`/api/contas-a-receber/${conta.id}`);
      toast.success(data?.message || 'Conta excluída com sucesso.');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir conta.');
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Baixa em lote ── */
  const handleReceberLote = async () => {
    if (selected.size === 0) { toast.warn('Selecione ao menos uma conta.'); return; }
    if (!window.confirm(`Confirmar recebimento de ${selected.size} conta(s)?`)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/contas-a-receber/receber-lote', {
        ids: [...selected],
        conta_bancaria_id: contaDestino || null,
      });
      toast.success(data.message);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro no recebimento em lote.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Computed ── */
  const contasFiltradas = useMemo(() => {
    if (!iaAtivo || !iaResult?.ids?.length) return contas;
    const idSet = new Set(iaResult.ids);
    return contas.filter(c => idSet.has(c.id));
  }, [contas, iaAtivo, iaResult]);

  const receivable  = useMemo(() => contasFiltradas.filter(c => c.status !== 'recebido'), [contasFiltradas]);
  const allChecked  = receivable.length > 0 && selected.size === receivable.length;
  const someChecked = selected.size > 0 && !allChecked;

  const toggleRow = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const toggleAll = () => allChecked ? setSelected(new Set()) : setSelected(new Set(receivable.map(c => c.id)));

  const totalSelected = useMemo(
    () => contas.filter(c => selected.has(c.id)).reduce((a, c) => a + parseFloat(c.valor || 0), 0),
    [contas, selected]
  );

  const totals = useMemo(() => ({
    pendente: contas.filter(c => c.status === 'pendente').reduce((a, c) => a + parseFloat(c.valor || 0), 0),
    vencido:  contas.filter(c => c.status === 'vencido').reduce((a, c)  => a + parseFloat(c.valor || 0), 0),
    recebido: contas.filter(c => c.status === 'recebido').reduce((a, c) => a + parseFloat(c.valor || 0), 0),
    patio:    contas.filter(c => c.origem_tipo === 'patio' && c.status !== 'recebido').reduce((a, c) => a + parseFloat(c.valor || 0), 0),
  }), [contas]);

  const clienteNome = (c) => c.cliente_nome || c.cliente_nome_cadastro || null;

  /* ─── Render ─────────────────────────────────────────────────── */
  return (
    <div className="car-root">

      {/* ── HEADER ── */}
      <div className="car-header">
        <div className="car-header-text">
          <h1 className="car-title">Contas a Receber</h1>
          <p className="car-subtitle">OS concluídas e diárias de pátio são lançadas automaticamente</p>
        </div>

        {/* KPI Strip */}
        <div className="car-kpi-strip">
          {loadingKpi ? (
            <span className="kpi-loading"><FaSpinner className="spin" /> carregando…</span>
          ) : (<>
            <button
              className={`kpi-chip kpi-pending ${filters.status === 'pendente' ? 'active' : ''}`}
              onClick={() => handleKpiClick('pendente')}
            >
              <span className="chip-bullet" />
              <div className="chip-body">
                <span className="chip-label">Pendente</span>
                <span className="chip-val">{fmtCur(kpis.total_pendente)}</span>
              </div>
            </button>
            <button
              className={`kpi-chip kpi-overdue ${filters.status === 'vencido' ? 'active' : ''}`}
              onClick={() => handleKpiClick('vencido')}
            >
              <span className="chip-bullet" />
              <div className="chip-body">
                <span className="chip-label">Vencido</span>
                <span className="chip-val">{fmtCur(kpis.total_vencido)}</span>
              </div>
            </button>
            <span className="kpi-chip kpi-today">
              <span className="chip-bullet" />
              <div className="chip-body">
                <span className="chip-label">Vence hoje</span>
                <span className="chip-val">{fmtCur(kpis.vence_hoje)}</span>
              </div>
            </span>
            <span className="kpi-chip kpi-received">
              <span className="chip-bullet" />
              <div className="chip-body">
                <span className="chip-label">Recebido/mês</span>
                <span className="chip-val">{fmtCur(kpis.recebido_mes)}</span>
              </div>
            </span>
            {totals.patio > 0 && (
              <button
                className={`kpi-chip kpi-patio ${filters.origem_tipo === 'patio' ? 'active' : ''}`}
                onClick={() => setFilter('origem_tipo', filters.origem_tipo === 'patio' ? '' : 'patio')}
                title="Filtrar diárias de pátio"
              >
                <span className="chip-bullet" />
                <div className="chip-body">
                  <span className="chip-label">Pátio</span>
                  <span className="chip-val">{fmtCur(totals.patio)}</span>
                </div>
              </button>
            )}
          </>)}
        </div>

        <div className="car-header-actions">
          <WizardTriggerBtn mode="receber" onClick={() => setWizardOpen(true)} />
          <RegenteLancamento inline onSuccess={reload} />
          <button className="car-btn-new" onClick={() => { setEditItem(null); setModalMode(null); setModalOpen(true); }}>
            <FaPlus /> Nova Receita
          </button>
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div className="car-filter-bar">

        <div className="cfb-main-row">
          <div className="cfb-dates">
            <label>De</label>
            <input
              type="date"
              value={filters.dataInicio}
              onChange={e => setFilter('dataInicio', e.target.value)}
            />
            <label>Até</label>
            <input
              type="date"
              value={filters.dataFim}
              onChange={e => setFilter('dataFim', e.target.value)}
            />
          </div>

          <div className="cfb-periods">
            {QUICK_PERIODS.map(p => (
              <button
                key={p.id}
                className={`cfb-period-btn ${activePeriod === p.id ? 'active' : ''}`}
                onClick={() => applyPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="cfb-divider" />

          <button
            className={`cfb-more-btn ${filtersOpen ? 'open' : ''}`}
            onClick={() => setFiltersOpen(v => !v)}
          >
            <FaFilter />
            {activeFilterCount > 0 && <span className="cfb-badge">{activeFilterCount}</span>}
            Mais filtros
            <FaChevronDown className={`cfb-chevron ${filtersOpen ? 'rotated' : ''}`} />
          </button>

          {hasSecondaryFilters && (
            <button className="cfb-clear-btn" onClick={clearSecondaryFilters}>
              <FaTimes /> Limpar
            </button>
          )}
        </div>

        {filtersOpen && (
          <div className="cfb-expanded">
            <div className="cfb-field">
              <label>STATUS</label>
              <select value={filters.status} onChange={e => setFilter('status', e.target.value)}>
                <option value="">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="vencido">Vencido</option>
                <option value="recebido">Recebido</option>
              </select>
            </div>
            <div className="cfb-field">
              <label>ORIGEM</label>
              <select value={filters.origem_tipo} onChange={e => setFilter('origem_tipo', e.target.value)}>
                <option value="">Todas</option>
                <option value="os_servico">OS de Serviço</option>
                <option value="patio">Diárias de Pátio</option>
                <option value="aprovacao_ap">Reembolso Aprovado</option>
                <option value="manual">Lançamento Manual</option>
                <option value="venda_manual">Venda Manual</option>
              </select>
            </div>
            <div className="cfb-field">
              <label>Nº DA NOTA</label>
              <input
                type="text"
                placeholder="NF-e, NFSe, Recibo..."
                value={filters.numero_nota}
                onChange={e => setFilter('numero_nota', e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Linha de busca IA */}
        <div className="cfb-ia-row">
          <div className="cfb-ia-wrap">
            <FaMagic className="cfb-ia-icon" />
            <input
              type="text"
              className="cfb-ia-input"
              placeholder='Busca inteligente com IA — ex: "notas vencidas acima de R$500" · "OS do cliente João" · "não recebidas de maio"'
              value={iaBusca}
              onChange={e => setIaBusca(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleIaBusca()}
            />
            <button
              className="cfb-ia-btn"
              onClick={handleIaBusca}
              disabled={iaLoading || !iaBusca.trim()}
            >
              {iaLoading ? <FaSpinner className="spin" /> : 'Buscar com IA'}
            </button>
            {iaAtivo && (
              <button className="cfb-ia-clear" onClick={clearIaBusca} title="Limpar busca IA">
                <FaTimes />
              </button>
            )}
          </div>

          {iaAtivo && iaResult && (
            <div className={`cfb-ia-banner ${iaResult.total_encontrado === 0 ? 'ia-empty' : 'ia-hit'}`}>
              <FaMagic className="ia-banner-star" />
              <span>
                <strong>{iaResult.total_encontrado}</strong>{' '}
                conta{iaResult.total_encontrado !== 1 ? 's' : ''} encontrada{iaResult.total_encontrado !== 1 ? 's' : ''}
                {iaResult.resumo ? ` · ${iaResult.resumo}` : ''}
              </span>
              <button className="ia-banner-close" onClick={clearIaBusca}><FaTimes /></button>
            </div>
          )}
        </div>
      </div>

      {/* ── SUMMARY STRIP ── */}
      <div className="car-summary-strip">
        <div className="css-totals">
          <div className="css-item css-pending">
            <span className="css-label">A receber</span>
            <span className="css-val">{fmtCur(totals.pendente + totals.vencido)}</span>
          </div>
          {totals.vencido > 0 && (
            <div className="css-item css-overdue">
              <span className="css-label">⚠ Vencido</span>
              <span className="css-val">{fmtCur(totals.vencido)}</span>
            </div>
          )}
          <div className="css-item css-received">
            <span className="css-label">Recebido</span>
            <span className="css-val">{fmtCur(totals.recebido)}</span>
          </div>
          {totals.patio > 0 && (
            <div className="css-item css-patio">
              <span className="css-label">🅿 Pátio pend.</span>
              <span className="css-val">{fmtCur(totals.patio)}</span>
            </div>
          )}
          {iaAtivo && iaResult && (
            <div className="css-item css-ia">
              <span className="css-label">🤖 IA selecionou</span>
              <span className="css-val">
                {iaResult.total_encontrado} conta{iaResult.total_encontrado !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        <div className="css-spacer" />

        <div className="receive-inline">
          <div className="dest-wrap">
            <FaUniversity className="dest-icon" />
            <select value={contaDestino} onChange={e => setContaDestino(e.target.value)} className="dest-select">
              <option value="">Caixa Principal</option>
              {contasBancarias.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
            </select>
          </div>
          <button
            className="receive-btn"
            onClick={handleReceberLote}
            disabled={selected.size === 0 || submitting}
          >
            {submitting ? <FaSpinner className="spin" /> : <FaCheck />}
            {selected.size > 0
              ? `Receber ${selected.size} · ${fmtCur(totalSelected)}`
              : 'Receber selecionadas'}
          </button>
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="car-table-wrap">
        <table className="car-table">
          <thead>
            <tr>
              <th className="th-id">ID</th>
              <th className="th-date">
                <label className="th-check-label">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={el => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll}
                    disabled={receivable.length === 0}
                  />
                  <span>Vencimento</span>
                </label>
              </th>
              <th>Descrição</th>
              <th>Cliente</th>
              <th className="th-right">Valor</th>
              <th className="th-center">Status</th>
              <th className="th-origem">Origem</th>
              <th className="th-action">Ação</th>
            </tr>
          </thead>
          <tbody>
            {loadingTable ? (
              <tr><td colSpan={8} className="td-state">
                <FaSpinner className="spin" /> Buscando lançamentos…
              </td></tr>
            ) : contasFiltradas.length === 0 ? (
              <tr><td colSpan={8} className="td-state empty">
                <div className="empty-icon"><FaCalendarAlt /></div>
                <p>{iaAtivo ? 'IA não encontrou correspondências' : 'Nenhum lançamento encontrado'}</p>
                <small>
                  {iaAtivo
                    ? `Query: "${iaBusca}"`
                    : `${fmtDate(filters.dataInicio)} – ${fmtDate(filters.dataFim)}`}
                </small>
              </td></tr>
            ) : contasFiltradas.map(c => {
              const isOverdue  = c.status === 'vencido';
              const isPaid     = c.status === 'recebido';
              const isSelected = selected.has(c.id);
              const isIaMatch  = iaAtivo && iaResult?.ids?.includes(c.id);
              const sc         = STATUS_CFG[c.status] || STATUS_CFG.pendente;
              const nome       = clienteNome(c);
              const isDeleting = deletingId === c.id;
              const isAprovacao = c.origem_tipo === 'aprovacao_ap';

              return (
                <tr
                  key={c.id}
                  className={[
                    'car-row',
                    isOverdue  ? 'row-overdue'  : '',
                    isPaid     ? 'row-received' : '',
                    isSelected ? 'row-selected' : '',
                    isIaMatch  ? 'row-ia-match' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {/* ID */}
                  <td className="td-id">{c.id}</td>

                  {/* Vencimento + checkbox */}
                  <td className="td-date-cell">
                    <label className="date-cell-label">
                      <input
                        type="checkbox"
                        className="row-checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(c.id)}
                        disabled={isPaid}
                      />
                      <span className={`date-value ${isOverdue ? 'date-red' : ''} ${isPaid ? 'date-muted' : ''}`}>
                        {fmtDate(c.data_vencimento)}
                      </span>
                      {isOverdue && (
  <span className="overdue-badge" title="Vencido">
    <FaExclamationCircle />
  </span>
)}</label>
                  </td>

                  {/* Descrição + badges */}
                  <td className="td-desc">
                    <span className="desc-text" title={c.descricao}>{c.descricao}</span>
                    <div className="desc-badges">
                      {isAprovacao && (
                        <span
                          className="aprovacao-badge"
                          title={
                            c.aprovado_por_nome
                              ? `Aprovado por ${c.aprovado_por_nome}${c.aprovado_em ? ' em ' + fmtDate(c.aprovado_em) : ''}`
                              : 'Reembolso de A/P aprovado'
                          }
                        >
                          <FaCheckCircle size={9} /> Reembolso aprovado
                        </span>
                      )}
                      {c.pagador_nome && (
                        <span className="pagador-badge" title={`Pagador (quem reembolsa): ${c.pagador_nome}`}>
                          <FaUserTie size={9} /> {c.pagador_nome}
                        </span>
                      )}
                      {c.numero_nota && (
                        <span className="nota-badge" title={`Nota fiscal: ${c.numero_nota}`}>
                          <FaFileInvoiceDollar size={9} /> {c.numero_nota}
                        </span>
                      )}
                      {c.anexo_url && (
                        <a
                          href={`https://drive.google.com/file/d/${c.anexo_url}/view`}
                          target="_blank"
                          rel="noreferrer"
                          className="anexo-badge"
                          title="Ver comprovante"
                          onClick={e => e.stopPropagation()}
                        >
                          <FaPaperclip size={9} />
                        </a>
                      )}
                      {c.financeiro_id && (
                        <span className="fin-link-badge" title={`Financeiro #${c.financeiro_id}`}>
                          <FaLink size={9} /> Fin #{c.financeiro_id}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Cliente */}
                  <td className="td-client">
                    {nome
                      ? <span className="client-name" title={nome}>{nome}</span>
                      : <span className="client-empty">—</span>}
                  </td>

                  {/* Valor */}
                  <td className="td-valor">
                    <span className={isOverdue ? 'val-overdue' : isPaid ? 'val-paid' : ''}>
                      {fmtCur(c.valor)}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="td-status">
                    <span className={`pill ${sc.cls}`}>{sc.label}</span>
                  </td>

                  {/* Origem */}
                  <td className="td-origin"><OrigemTag conta={c} /></td>

                  {/* Ação */}
                  <td className="td-action">
                    <div className="action-wrap">
                      {isPaid ? (
                        <button
                          className="action-btn action-view"
                          onClick={() => { setEditItem(c); setModalMode(null); setModalOpen(true); }}
                          title="Ver detalhes"
                        >
                          <FaEye /> Ver
                        </button>
                      ) : (
                        <>
                          <button
                            className={`action-btn action-receive ${isOverdue ? 'action-urgent' : ''}`}
                            onClick={() => setBaixaTarget(c)}
                            title="Registrar recebimento"
                          >
                            <FaArrowRight /> Receber
                          </button>

                          {isAdminGeral && (
                            <>
                              <button
                                className="action-btn action-edit"
                                title="Editar conta"
                                onClick={() => handleEditar(c)}
                                disabled={isDeleting}
                              >
                                <FaPen />
                              </button>
                              <button
                                className="action-btn action-convert"
                                title="Converter para Conta a Pagar"
                                onClick={() => setConversaoTarget({ conta: c, tipo: 'ar_to_ap' })}
                                disabled={isDeleting}
                              >
                                <FaExchangeAlt />
                              </button>
                              <button
                                className="action-btn action-delete"
                                title="Excluir conta"
                                onClick={() => handleExcluir(c)}
                                disabled={isDeleting}
                              >
                                {isDeleting ? <FaSpinner className="spin" /> : <FaTrashAlt />}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>

          {contasFiltradas.length > 0 && (
            <tfoot>
              <tr className="tfoot-row">
                <td colSpan={4} className="tfoot-label">
                  {contasFiltradas.length} lançamento{contasFiltradas.length !== 1 ? 's' : ''}
                  {iaAtivo && ' · filtro IA ativo'}
                </td>
                <td className="tfoot-val">
                  {fmtCur(contasFiltradas.reduce((a, c) => a + parseFloat(c.valor || 0), 0))}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── BATCH BAR ── */}
      {selected.size > 0 && (
        <div className="car-batch-bar">
          <div className="batch-left">
            <span className="batch-count">{selected.size}</span>
            <span className="batch-label">conta{selected.size > 1 ? 's' : ''} selecionada{selected.size > 1 ? 's' : ''}</span>
            <span className="batch-sep">·</span>
            <span className="batch-total">{fmtCur(totalSelected)}</span>
          </div>
          <div className="batch-right">
            <button className="batch-clear" onClick={() => setSelected(new Set())}><FaTimes /></button>
            <button className="batch-receive" onClick={handleReceberLote} disabled={submitting}>
              {submitting ? <FaSpinner className="spin" /> : <FaCheck />}
              Confirmar recebimento
            </button>
          </div>
        </div>
      )}

      {/* ── MODAIS ── */}
      {baixaTarget && (
        <BaixaModal
          conta={baixaTarget}
          onConfirm={handleBaixa}
          onClose={() => setBaixaTarget(null)}
          loading={baixaLoading}
        />
      )}

      {modalOpen && (
        <ModalContasAReceber
          isOpen={modalOpen}
          contaToEdit={editItem}
          modo={modalMode}
          onClose={() => { setModalOpen(false); setModalMode(null); }}
          onSave={reload}
        />
      )}

      {wizardOpen && (
        <WizardLancamento
          mode="receber"
          onClose={() => setWizardOpen(false)}
          onSave={reload}
        />
      )}

      {/* ── MODAL CONVERSÃO A/R → A/P ── */}
      {conversaoTarget && (
        <ModalConversaoConta
          conta={conversaoTarget.conta}
          tipo={conversaoTarget.tipo}
          onClose={() => setConversaoTarget(null)}
          onSuccess={() => { setConversaoTarget(null); reload(); }}
        />
      )}
    </div>
  );
}